import React from 'react';
import styled from 'styled-components';
import { reportError } from '../utils/telemetry';

/*
 * Catches a page that throws while rendering, so the sidebar and header stay up
 * and the next click works. Without it React unmounts the whole tree and the tab
 * goes white — which is what a failed page-chunk load looked like after a deploy.
 * App.js keys it on the path, so navigating away clears the error.
 */

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => p.theme.spacing.md};
  min-height: 40vh;
  padding: ${(p) => p.theme.spacing.lg};
  text-align: center;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const Title = styled.div`
  font-size: ${(p) => p.theme.fontSizes.lg};
  font-weight: ${(p) => p.theme.fontWeights.semibold};
  color: ${(p) => p.theme.colors.text};
`;

const Button = styled.button`
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text};
  font: inherit;
  cursor: pointer;
  &:hover { background: ${(p) => p.theme.colors.surfaceHover}; }
`;

export default class PageErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Page crashed:', error, info?.componentStack);
    reportError('crash', error?.message, { stack: String(error?.stack || '').slice(0, 500),
      component: String(info?.componentStack || '').slice(0, 500) });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Box>
        <Title>This page could not load</Title>
        <div>Usually a weak connection or a dashboard update. Reloading fixes it.</div>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Box>
    );
  }
}
